import { ConfigurationSourceDataMap, ConfigurationStatus, CoordinatingGroup, Institution, MasterGroup, Metadata, Participant, SourceData, SourceDataEventStatus, SourceDataFormStatus, Trial } from "@/api/dtos";
import { useMemo } from "react";
import { createFormConfiguration } from "../configuration/useFormConfiguration";
import { createFormVersionConfiguration } from "../configuration/useFormVersionConfiguration";

export enum FormExcludedReason {
    Disabled = 'The Case Report Form is disabled in the participant\'s allocation.',
    Status = 'The Case Report Form\'s Status is not included in the participant\'s allocation.',
    VersionDisabled = 'The Case Report Form version is disabled in the participant\'s allocation.'
}

export const createForm = (
    metadata?: Metadata, 
    trialConfigurationForms?: ConfigurationSourceDataMap['forms'], 
    configurationForms?: ConfigurationSourceDataMap['forms'], 
    sourceDataForms?: SourceData['forms'], 
    trialConfigurationFormVersions?: ConfigurationSourceDataMap['formVersions'], 
    configurationFormVersions?: ConfigurationSourceDataMap['formVersions'], 
    trial?: Trial, 
    masterGroup?: MasterGroup,
    coordinatingGroup?: CoordinatingGroup,
    institution?: Institution,
    participant?: Participant,
    isGlobal?: boolean,
    configurationSourceDataId?: number, 
    studyEventOid?: string,
    studyEventRepeat?: number,
    caseReportFormOid?: string,
    preview?: boolean,
    includeExcluded = false
) => {
    if (!studyEventRepeat) {
        return undefined;
    }

    const formConfiguration = createFormConfiguration(
        metadata,
        trialConfigurationForms,
        configurationForms,
        trial,
        isGlobal,
        configurationSourceDataId,
        studyEventOid,
        caseReportFormOid
    )

    if (!formConfiguration) {
        return undefined;
    }

    let formExcludeReason: FormExcludedReason | undefined = undefined;

    const sourceDataForm = sourceDataForms?.[studyEventOid!]?.[studyEventRepeat]?.[caseReportFormOid!]

    if (!formConfiguration.configuration?.statuses ||
        !formConfiguration.configuration.statuses.includes(sourceDataForm?.status ?? SourceDataFormStatus.None) ||
        !formConfiguration.configuration?.enabled) {
        if (!includeExcluded) {
            return undefined;
        }

        if (!formConfiguration.configuration?.enabled) {
            formExcludeReason = FormExcludedReason.Disabled;
        } else if (sourceDataForm?.status) {
            formExcludeReason = FormExcludedReason.Status;
        }
    }

    let caseReportFormVersionOid = sourceDataForm?.caseReportFormVersionOid;

    if (!caseReportFormVersionOid) {
        for (let versionOid in metadata?.formVersions?.[caseReportFormOid!]) {
            if (!caseReportFormVersionOid || (metadata?.formVersions?.[caseReportFormOid!]?.[versionOid]?.order ?? 0) > (metadata?.formVersions?.[caseReportFormOid!]?.[caseReportFormVersionOid]?.order ?? 0)) {
                caseReportFormVersionOid = versionOid;
            }
        }
    }
    

    const formVersionConfiguration = createFormVersionConfiguration(
        metadata,
        trialConfigurationFormVersions,
        configurationFormVersions,
        trial,
        isGlobal,
        configurationSourceDataId,
        studyEventOid,
        caseReportFormOid,
        caseReportFormVersionOid
    )

    if (!formVersionConfiguration?.configuration) {
        return undefined;
    }

    if (!formVersionConfiguration?.configuration?.enabled) {
        if (!includeExcluded) {
            return undefined;
        }

        formExcludeReason = FormExcludedReason.VersionDisabled;
    }
    
    const link = `/events/${studyEventOid}/${studyEventRepeat}/forms/${caseReportFormOid}`

    return {
        ...formConfiguration,
        formVersion: formVersionConfiguration,
        trial,
        masterGroup, 
        coordinatingGroup,
        institution,
        participant,
        preview,
        id: caseReportFormOid as any as number,
        status: ConfigurationStatus.Enabled,
        studyEventRepeat: studyEventRepeat,
        sourceDataForm: sourceDataForm,
        previewLink: `/${trial?.oid}/preview/participants/${participant?.studyNumber}${link}`,
        dataLink: `/${trial?.oid}${institution ? `/institutions/${institution.code}` : ''}/participants/${participant?.studyNumber}${link}`,
        excluded: formExcludeReason !== undefined,
        exclusionReason: formExcludeReason
    };
}

const useForm = (
    metadata?: Metadata, 
    trialConfiguration?: ConfigurationSourceDataMap, 
    configuration?: ConfigurationSourceDataMap, 
    sourceData?: SourceData,
    trial?: Trial, 
    masterGroup?: MasterGroup,
    coordinatingGroup?: CoordinatingGroup,
    institution?: Institution,
    participant?: Participant,
    studyEventOid?: string,
    studyEventRepeat?: number,
    caseReportFormOid?: string,
    preview?: boolean,
    includeExcluded = false
) => {
    return useMemo(() => {
        return createForm(
            metadata, 
            trialConfiguration?.forms, 
            configuration?.forms,
            sourceData?.forms, 
            trialConfiguration?.formVersions, 
            configuration?.formVersions,
            trial, 
            masterGroup,
            coordinatingGroup,
            institution,
            participant,
            configuration?.configuration?.isGlobal,
            configuration?.configuration?.id, 
            studyEventOid,
            studyEventRepeat,
            caseReportFormOid,
            preview,
            includeExcluded
        );
    }, [
        metadata, 
        trialConfiguration?.forms, 
        configuration?.forms, 
        sourceData?.forms, 
        trialConfiguration?.formVersions, 
        configuration?.formVersions, 
        trial, 
        masterGroup, 
        coordinatingGroup,
        institution, 
        participant, 
        studyEventOid, 
        studyEventRepeat,
        caseReportFormOid, 
        preview,
        includeExcluded
    ]);
};

export default useForm;